import {
  AmplitudeAnalyticsService,
  AnalyticsManager,
  GoogleAnalyticsService,
  InternalAnalyticsService,
} from '@oy/analytics';
import {
  breadcrumbsIntegration,
  dedupeIntegration,
  defaultStackParser,
  globalHandlersIntegration,
  init,
  linkedErrorsIntegration,
  makeFetchTransport,
  replayIntegration,
  setUser,
} from '@sentry/browser';
import { BrowserClientOptions } from '@sentry/browser/build/npm/types/client';
import TagManager from 'react-gtm-module';
import { isValidDataUrl } from 'src/utilities/helpers';
import { isBlacklistedXhrUrl } from 'src/utilities/sentry';
import { storage } from 'src/utilities/Storage';

// Config Bodies
const sentryConfig: BrowserClientOptions = {
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENV,
  tracesSampleRate: 0.5,
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.1,

  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  replaysOnErrorSampleRate: 0.5,

  transport: makeFetchTransport,
  stackParser: defaultStackParser,

  integrations: [
    breadcrumbsIntegration(),
    globalHandlersIntegration(),
    linkedErrorsIntegration(),
    dedupeIntegration(),
    replayIntegration({
      maskAllInputs: true,
      stickySession: true,
      minReplayDuration: 15000,
      unblock: ['.sentry-unblock, [data-sentry-unblock]'],
      unmask: ['.sentry-unmask, [data-sentry-unmask]'],
    }),
  ],
  ignoreErrors: [
    'Java exception was raised during method invocation',
    'Non-Error exception captured',
    'Non-Error promise rejection captured',
    /Request failed with status code 401/i,
    /status code/i,
  ],
  beforeBreadcrumb(breadcrumb) {
    switch (breadcrumb.category) {
      case 'fetch':
        if (isValidDataUrl(breadcrumb.data?.url)) {
          return null;
        }
        break;
      case 'xhr':
        if (isBlacklistedXhrUrl(breadcrumb.data?.url)) {
          return null;
        }
        break;
      default:
        break;
    }
    return breadcrumb;
  },
};

const gtmConfig = {
  gtmId: import.meta.env.VITE_GTM_ID,
};

// Initialization
export const registerAnalytics = () => {
  const platform = 'desktop_dashboard';

  const auth = storage.get('auth');
  const sessionId = auth?.session_id;

  AnalyticsManager.instance().register(
    new AmplitudeAnalyticsService(
      import.meta.env.VITE_AMPLITUDE_API_KEY,
      import.meta.env.VITE_ENV,
    ),
  );
  AnalyticsManager.instance().register(
    new InternalAnalyticsService(
      import.meta.env.VITE_AMPLITUDE_API_KEY!,
      import.meta.env.VITE_INTERNAL_ANALYTICS_URL!,
    ),
  );
  AnalyticsManager.instance().register(
    new GoogleAnalyticsService({
      measurementIds: [
        import.meta.env.VITE_GA_MEASUREMENT_ID_1,
        import.meta.env.VITE_GA_MEASUREMENT_ID_2,
      ],
    }),
  );
  AnalyticsManager.instance().setPlatform(platform);
  AnalyticsManager.instance().setSessionId(sessionId);
};

export const initSentry = () => {
  const auth = storage.get('auth');
  const username = auth?.user?.username;

  init(sentryConfig);
  setUser({
    username,
  });
};

export const initGtm = () => {
  TagManager.initialize(gtmConfig);
};
